import { type AppInfo } from 'api/types/AppInfo';
import { type ChapterContentPage } from 'api/types/ChapterContent/ChapterContentPage';
import { type RawChapterContentPage } from 'api/types/ChapterContent/ChapterContentPage/RawChapterContentPage';

import { convertImgixUrl } from 'helpers/convertImgixUrl';
import { hash } from 'helpers/hash';

import { parseQuestionData, type RawChapterQuestionPage } from './parseQuestionData';

type Slideshow = NonNullable<RawChapterContentPage[`link_slideshow_${'1' | '2' | '3'}`]>;

const isSlideshow = (value: RawChapterContentPage[keyof RawChapterContentPage]): value is Slideshow => {
  if (Array.isArray(value) && value.length > 0) {
    return (value as Slideshow).every((slide) => {
      return slide.link_id !== undefined;
    });
  }

  return false;
};

const parsePageText = (text: string) => {
  return text.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/[’]/, "'");
};

const isRawQuestion = (data: RawChapterContentPage | RawChapterQuestionPage): data is RawChapterQuestionPage => {
  if ((data as RawChapterQuestionPage)?.question) {
    return true;
  }

  return false;
};

const parseType = <T extends RawChapterContentPage>(data: T): ChapterContentPage['type'] => {
  if (isRawQuestion(data)) {
    return data.type as ChapterContentPage['type'];
  }

  switch (data.pageType) {
    case '':
      return 'ContentPageText';

    case 'autoPlay':
      return 'ContentPageVideo';

    case 'Photo':
      return 'ContentPageImage';

    case 'Final-Page':
      return 'ContentPageEndScreen';

    default:
      throw new Error(`Unknown page type: ${data.pageType as unknown as string}`);
  }
};

type Links = ChapterContentPage['links'];
type JwLinks = ChapterContentPage['linkJw'];

const guessLinkType = (url: string): Links[number]['type'] => {
  const match = /^.*\.(?<ext>.*?)$/g.exec(url)?.groups;

  if (match) {
    switch (match.ext) {
      case 'pdf':
        return 'pdf';

      case 'mp4':
        return 'video';

      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'infographic';

      default:
        return 'weblink';
    }
  }

  return 'weblink';
};

const correctLink = (url: string, appId: string) => {
  if (url.startsWith('http') && !url.endsWith('.pdf')) {
    return url;
  }
  if (url.endsWith('.pdf')) {
    return `https://stack.travpromobile.com/viewer.php?file=${url}#zoom=page-fit`;
  }
  return `https://front.travpromobile.com/${appId}/${url}`;
};

export const parseChapterContent = (rawData: RawChapterContentPage, app: AppInfo, i: number) => {
  const data = { ...rawData, pageNumber: i + 1 };

  if (isRawQuestion(data)) {
    const image = convertImgixUrl(app.questionHeader, app.questionHeader.match(/^https?/) ? undefined : app.id);

    return parseQuestionData({ ...data, image }, app);
  }

  const links: Links = [];
  const linkJw: JwLinks = [];

  Object.entries(data).forEach((entry) => {
    type Key = keyof RawChapterContentPage;
    const [key, value] = entry as [Key, RawChapterContentPage[Key]];
    if (key.startsWith('linkJW')) {
      linkJw.push(value as string);
      return;
    }

    const uniquelyHandledTypes = ['slideshow'];
    const slideshowMatch = /^link_slideshow_([0-9])*$/g.exec(key);

    // As the slideshow comoponent is a special case (updated to no longer use the comma separated values at link1 param), this section handles the slideshow links. Array Above prevents duplicates from the 'normal handling'.
    if (slideshowMatch !== null && isSlideshow(value)) {
      const [, index] = slideshowMatch;

      const resources = value.reduce(
        (acc, { url = '', photo_credits: credits = '' }) => {
          return [
            ...acc,
            {
              src: url.startsWith('http') ? url : `https://front.travpromobile.com/${app.id}/${url}`,
              credits,
            },
          ];
        },
        [] as NonNullable<Links[number]['resources']>,
      );

      const newLink: Links[number] = {
        id: index, // Temporary value
        resources,
        type: 'slideshow',
        text: data[`linkTextHtml${index}` as Key] as string,
      };

      links.push({
        ...newLink,
        id: hash({ ...newLink }, 10),
      });

      return;
    }

    const linkMatch = /^link([0-9])*$/g.exec(key);

    if (linkMatch && typeof value === 'string') {
      type ValidIndices = '1' | '2' | '3' | '4' | '5' | '6';
      type LinkTypeKey = `linkType${ValidIndices}`;

      const [, index] = linkMatch;

      const key = `link${index}` as Key;
      const value = data[key] as string;
      const typeKey = `linkType${index}` as Key;
      const typeValue = data[typeKey] as RawChapterContentPage[LinkTypeKey];

      const newType = typeValue as Links[number]['type'];
      if (!uniquelyHandledTypes.includes(newType) && value.length > 0) {
        const newLink: Links[number] = {
          id: index, // Temporary value
          resources: [
            {
              src: correctLink(value, app.id),
            },
          ],
          type: newType || guessLinkType(value),
          text: data[`linkTextHtml${index}` as Key] as string,
        };

        newLink.id = hash({ ...newLink }, 10);

        links.push(newLink);
      }
    }
  });

  const parsedData: ChapterContentPage = {
    comment: data.comment || null,
    date: new Date(data?.date || 0),
    image: convertImgixUrl(data.image, app.id),
    imageCredits: data.imageCredits || null,
    links,
    linkJw: linkJw.filter((url) => {
      return url.length > 0;
    }),
    pageNumber: Number(data.pageNumber),
    id: data.upID,
    type: parseType(data),
    salesTip: data.salesTip,
    subtitle: data.subtitle,
    summary: data.summary,
    text: parsePageText(data.text),
    title: data.title,
  };

  Object.entries(parsedData).forEach((entry) => {
    const [key, value] = entry as [keyof ChapterContentPage, unknown];

    if (typeof value === 'string' && value.length === 0) {
      (parsedData[key] as string | null) = null;
    }
  });

  return parsedData;
};
